import './style.css'
import { connectToDiscord, displayName, hasClientId, isInsideDiscord } from './discord'
import { ServerClock, type GameState } from './game'
import { describeError, log } from './log'
import { RoomConnection, type ConnectionStatus } from './room'
import { t, type Lang } from './i18n'
import { isPrivacyRoute } from './privacy'
import {
  langOf,
  phaseTitle,
  playerCountLabel,
  renderGame,
  renderPrivacy,
  renderSplash,
  type ViewActions,
} from './views'

const app = document.querySelector<HTMLDivElement>('#app')
if (!app) throw new Error('#app niet gevonden')

app.innerHTML = `
  <div class="shell">
    <header>
      <div class="brand">
        <span class="brand-name">Treehouse Quiz</span>
        <h1 id="phase-title">Verbinden</h1>
      </div>
      <div class="header-right">
        <span class="playercount" id="playercount"></span>
        <span class="status" id="status" data-state="idle">niet verbonden</span>
      </div>
    </header>

    <section class="panel" id="notice-panel" hidden>
      <div class="notice" id="notice"></div>
    </section>

    <main id="board"></main>
  </div>
`

const statusEl = app.querySelector<HTMLElement>('#status')!
const titleEl = app.querySelector<HTMLElement>('#phase-title')!
const headerEl = app.querySelector<HTMLElement>('header')!
const playerCountEl = app.querySelector<HTMLElement>('#playercount')!
const noticePanel = app.querySelector<HTMLElement>('#notice-panel')!
const noticeEl = app.querySelector<HTMLElement>('#notice')!
const boardEl = app.querySelector<HTMLElement>('#board')!

const clock = new ServerClock()
let currentState: GameState | null = null
let myUserId = ''
// Tot de eerste staat binnen is weten we de taalkeuze nog niet.
let lang: Lang = 'nl'
let lastStatus: ConnectionStatus = 'idle'

// De intro ("jij omschrijft") is een korte client-side animatie aan het begin
// van de preview. De server kent die fase niet, want de klok staat toch stil
// en dan hoeft er niets gesynchroniseerd te worden.
const INTRO_MS = 2200
let introKey = ''
let introUntil = 0
let introTimer: ReturnType<typeof setTimeout> | undefined

// Zolang er nog geen spelstaat binnen is staat het laadscherm er.
let loading = true

function showLoading(key: string): void {
  if (!loading) return
  headerEl.hidden = true
  renderSplash(boardEl, key, lang)
}

function setStatus(status: ConnectionStatus): void {
  lastStatus = status
  statusEl.dataset.state = status
  statusEl.textContent = t(lang, `status.${status}`)
}

/** Bewust via textContent, want de tekst kan van de server komen. */
function showNotice(title: string, body: string): void {
  noticeEl.replaceChildren()
  const strong = document.createElement('strong')
  strong.textContent = title
  noticeEl.append(strong, ' ', body)
  noticePanel.hidden = false
}

// De klok tekent zichzelf los van de spelstaat bij, zodat hij vloeiend loopt
// zonder dat de server dertig keer per seconde iets hoeft te sturen.
/** Telt de wachttijd af op de aan-knop van meeluisteren.
 *
 *  Tijdens die minuut komt er geen nieuwe staat binnen, dus zonder deze
 *  aftelling zou de knop stil op "Aan" blijven staan en niets doen. */
function tickCooldown(): void {
  const knop = document.querySelector<HTMLButtonElement>('#listen-cooldown')
  if (!knop) return

  const klaar = Number(knop.dataset.readyAt ?? 0)
  const over = Math.ceil(klaar - clock.now())
  const label = knop.dataset.label ?? ''

  if (over <= 0) {
    knop.disabled = false
    knop.textContent = label
    knop.removeAttribute('id')
    return
  }
  knop.textContent = `${label} (${over}s)`
}

function tickTimer(): void {
  requestAnimationFrame(tickTimer)
  tickCooldown()

  const bar = document.querySelector<HTMLElement>('#timer-bar')
  const text = document.querySelector<HTMLElement>('#timer-text')
  if (!bar || !text || !currentState?.turn) return

  const remaining = clock.remaining(currentState.turn.ends_at)
  const fraction = Math.max(0, Math.min(1, remaining / currentState.turn_seconds))
  const low = remaining <= 5
  bar.style.width = `${fraction * 100}%`
  bar.dataset.low = String(low)
  text.dataset.low = String(low)
  // Hele seconden op de grote klok: tienden zijn onleesbaar en onrustig.
  text.textContent = String(Math.ceil(remaining))
}
requestAnimationFrame(tickTimer)

/** Houdt bij of de intro van deze beurt al voorbij is. */
function trackIntro(state: GameState): void {
  if (state.phase !== 'preview' || !state.turn) {
    introKey = ''
    return
  }
  const key = `${state.turns_played}:${state.turn.describer_id}`
  if (key === introKey) return

  introKey = key
  introUntil = Date.now() + INTRO_MS
  if (introTimer) clearTimeout(introTimer)
  // Na de intro nog een keer tekenen, anders blijft hij staan tot het
  // volgende bericht van de server.
  introTimer = setTimeout(() => {
    if (currentState && pendingActions) draw(pendingActions)
  }, INTRO_MS)
}

let pendingActions: ViewActions | null = null

// Zolang dit aan staat blijft de privacyverklaring in beeld, ook als er
// ondertussen een nieuwe staat binnenkomt.
let privacyOpen = false

function showPrivacy(actions: ViewActions): void {
  privacyOpen = true
  headerEl.hidden = false
  titleEl.textContent = ''
  renderPrivacy(boardEl, lang, () => {
    privacyOpen = false
    draw(actions)
  })
}

function draw(actions: ViewActions): void {
  if (!currentState) return
  loading = false
  if (privacyOpen) return
  pendingActions = actions
  const inRound = currentState.phase === 'preview' || currentState.phase === 'playing'
  headerEl.hidden = inRound
  titleEl.textContent = phaseTitle(currentState)
  playerCountEl.textContent = playerCountLabel(currentState)
  renderGame(boardEl, currentState, myUserId, actions, Date.now() < introUntil)
}

interface RoomIdentity {
  accessToken?: string
  devName?: string
  /** Waar de room in Discord hangt. Zonder deze twee weet de backend niet
   *  welk voicekanaal de meeluisterende bot in moet. */
  channelId?: string | null
  guildId?: string | null
}

function connectRoom(instanceId: string, identity: RoomIdentity): void {
  // De acties verwijzen naar room voordat die bestaat. Dat kan, want ze
  // worden pas aangeroepen als de gebruiker ergens op tikt, en dan staat de
  // verbinding er allang.
  const send = (payload: Record<string, unknown>): void => room.send(payload)

  const actions: ViewActions = {
    joinTeam: (teamId) => send({ op: 'join_team', team_id: teamId }),
    startGame: () => send({ op: 'start_game' }),
    wordResult: (correct) => send({ op: 'word_result', correct }),
    nextTurn: () => send({ op: 'next_turn' }),
    backToLobby: () => send({ op: 'back_to_lobby' }),
    setOption: (key, value) => send({ op: 'set_option', key, value }),
    resetGame: () => send({ op: 'reset_game' }),
    ready: () => send({ op: 'ready' }),
    openPrivacy: () => showPrivacy(actions),
  }

  const room = new RoomConnection(
    { instanceId, ...identity },
    {
      onStatus: setStatus,
      onMessage: (message) => {
        switch (message.op) {
          case 'welcome':
            myUserId = message.you.user_id
            clock.sync(message.server_time)
            log('ok', `Room binnen als ${message.you.name}`)
            break
          case 'state': {
            clock.sync(message.game.server_time)
            currentState = message.game
            trackIntro(message.game)
            const next = langOf(message.game)
            if (next !== lang) {
              // Taal gewisseld in de lobby: ook de kop en de statuspil mee.
              lang = next
              setStatus(lastStatus)
            }
            draw(actions)
            break
          }
          case 'pong':
            clock.sync(message.server_time)
            break
          case 'error':
            break
        }
      },
    },
  )

  room.connect()
}

async function startInDiscord(): Promise<void> {
  const session = await connectToDiscord(showLoading)
  log('info', `${displayName(session.user)} op ${session.platform.platform}`)
  showLoading('loading.room')
  connectRoom(session.instanceId, {
    accessToken: session.accessToken,
    channelId: session.channelId,
    guildId: session.guildId,
  })
}

function startStandalone(): void {
  // Buiten Discord om kunnen we de SDK-handshake niet doen, maar het spel
  // wel spelen. De backend accepteert dit alleen zolang allow_dev_rooms in
  // backend/config.json aan staat.
  const devName = `dev-${Math.random().toString(36).slice(2, 6)}`
  const start = document.createElement('button')
  start.textContent = `Meedoen als ${devName}`
  start.addEventListener('click', () => {
    start.disabled = true
    showLoading('loading.room')
    connectRoom('local-dev', { devName })
  })
  noticeEl.append(document.createElement('br'), start)
}

async function main(): Promise<void> {
  // Discord opent de privacylink buiten de client om. Dan hoeft er geen
  // handshake te gebeuren, alleen de tekst.
  if (isPrivacyRoute()) {
    loading = false
    headerEl.hidden = true
    renderPrivacy(boardEl, lang)
    return
  }

  log('info', 'Activity geladen')
  showLoading('loading.start')

  if (!hasClientId()) {
    loading = false
    boardEl.replaceChildren()
    headerEl.hidden = false
    log('error', 'Geen DISCORD_CLIENT_ID ingevuld')
    showNotice(
      'Geen client ID.',
      'Vul DISCORD_CLIENT_ID in de .env in de projectroot in en herstart de dev-server.',
    )
    return
  }

  if (!isInsideDiscord()) {
    loading = false
    boardEl.replaceChildren()
    headerEl.hidden = false
    log('warn', 'Geen frame_id in de URL, we draaien buiten Discord')
    showNotice(
      'Niet binnen Discord geopend.',
      'De SDK-handshake werkt alleen in de Discord-client. Je kunt het spel hier wel testen.',
    )
    startStandalone()
    return
  }

  try {
    await startInDiscord()
  } catch (error) {
    loading = false
    boardEl.replaceChildren()
    headerEl.hidden = false
    log('error', `Handshake mislukt: ${describeError(error)}`)
    setStatus('closed')
    showNotice('Handshake mislukt.', describeError(error))
  }
}

void main()
